// Account & shelter lifecycle integrity — the single source of truth for "what
// are the consequences of deactivating X?". Pure functions only (data in →
// verdict out, no I/O), so the UI confirm dialogs and any future automation
// share one set of rules that can never drift between screens. Mirrors the
// pattern of services/approvals.ts and services/requestLifecycle.ts.
//
// Plan + resolved decisions: docs/account-shelter-lifecycle-plan.md.
//
// Core definitions:
//   - Active worker for a shelter = a profile with role `shelter` (NOT `combined`
//     - combined is internal responder+picker staff, not a shelter worker),
//     NOT deactivated, that has a user_shelters membership for that shelter code.
//     (user_shelters is the canonical membership source — profiles.shelter_code
//     alone is not enough; see the 2026-06-29 provisioning fix.)
//   - In-flight request = status in ACTIVE_REQUEST_STATUSES (Submitted /
//     Part Processed). Draft is the shelter's own unsent work; Fully Processed is
//     terminal — neither counts as in-flight.

import { isTestCode } from "../crm/audience/audienceFilter";
import { ACTIVE_REQUEST_STATUSES, ROLES } from "../utils/enums";

export interface LifecycleProfile {
  id: string;
  role: string;
  deactivated?: boolean;
  display_name?: string | null;
  // Contact-model Phase 2b: the account email/name are the staff_* columns
  // (user_email/first_name/last_name are dropped in Phase 3).
  staff_user_email?: string | null;
  // The account name, passed through to AttachedUser for the shelter detail's
  // Users tab (Phase 3 of the shelter-admin redesign). Contact-model Phase 3
  // (Stage 1 de-wire): the legacy primary_contact_label / phones are no longer
  // read here — the per-service contact lives on the user_shelters membership.
  staff_first_name?: string | null;
  staff_last_name?: string | null;
}

export interface LifecycleMembership {
  user_id: string;
  shelter_code: string;
  // THE WALL: the membership IS the contact, so a caseworker's name lives here —
  // never on profiles.staff_*, which is Zephyr staff only.
  // See docs/staff-caseworker-separation-plan.md.
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}

export interface LifecycleRequest {
  shelter_code?: string | null;
  status?: string | null;
}

export interface WorkerRef {
  id: string;
  display_name: string;
  user_email: string;
}

export interface UserDeactivationAssessment {
  // Shelter codes that would be left with ZERO active workers if this user were
  // deactivated — i.e. orphaned. Empty = safe to deactivate with no shelter impact.
  orphanedShelters: string[];
}

export interface ShelterDeactivationAssessment {
  // Active workers currently attached to the shelter (to be cascade-deactivated
  // and shown to the admin). Decision 2: cascade.
  activeWorkers: WorkerRef[];
  // In-flight requests for the shelter. Informational only — they are NOT
  // cancelled, and office staff can still process them (decision 4).
  inflightRequestCount: number;
}

// Roles whose holders can submit requests for a shelter. `combined` is INTERNAL
// Zephyr staff (responder + picker), NOT a shelter caseworker, so it is
// intentionally absent (2026-06-30 combined-role clarification).
const SHELTER_WORKER_ROLES: readonly string[] = [ROLES.SHELTER];
const ACTIVE_STATUS_VALUES: readonly string[] = ACTIVE_REQUEST_STATUSES;

function isActiveWorkerProfile(p: LifecycleProfile | undefined): p is LifecycleProfile {
  return !!p && !p.deactivated && SHELTER_WORKER_ROLES.includes(p.role);
}

function toWorkerRef(p: LifecycleProfile): WorkerRef {
  return { id: p.id, display_name: p.display_name ?? "", user_email: p.staff_user_email ?? "" };
}

// A user attached to a shelter (for the admin shelter-detail "attached users"
// list — shows EVERYONE with a membership, active or deactivated, any role).
export interface AttachedUser {
  id: string;
  display_name: string;
  user_email: string;
  role: string;
  deactivated: boolean;
  first_name: string;
  last_name: string;
}

// Map of shelter_code → every profile attached via user_shelters (incl. deactivated
// users and non-worker roles), de-duplicated. Used by the admin shelter detail so
// an admin can see who is attached before editing/deactivating a shelter.
export function attachedUsersByShelter(
  memberships: LifecycleMembership[],
  profiles: LifecycleProfile[]
): Map<string, AttachedUser[]> {
  const byId = new Map<string, LifecycleProfile>(profiles.map((p) => [p.id, p]));
  const out = new Map<string, AttachedUser[]>();
  for (const m of memberships) {
    const p = byId.get(m.user_id);
    if (!p) continue;
    const list = out.get(m.shelter_code) ?? [];
    if (!list.some((u) => u.id === p.id)) {
      list.push({
        id: p.id,
        display_name: p.display_name ?? "",
        // The contact email/name come from the MEMBERSHIP — this list is the
        // people attached to a DFV Service, i.e. its contacts. profiles.staff_* is
        // Zephyr staff only (the wall). Falls back to the profile only so a row
        // still renders if a membership predates the contact fields.
        user_email: m.email ?? p.staff_user_email ?? "",
        role: p.role ?? "",
        deactivated: !!p.deactivated,
        first_name: m.first_name ?? p.staff_first_name ?? "",
        last_name: m.last_name ?? p.staff_last_name ?? "",
      });
    }
    out.set(m.shelter_code, list);
  }
  return out;
}

// Does a shelter match the admin Shelters-list search? Matches the shelter's
// own name / code / state, OR any attached user's name / email — so once the
// global Caseworkers roster is retired (Phase 3), typing a caseworker's name
// still surfaces their shelter(s). Pure; empty query matches everything.
export function shelterMatchesQuery(
  shelter: {
    shelter_name?: string | null;
    shelter_code?: string | null;
    state?: string | null;
    sales_group?: string | null;
  },
  query: string,
  attachedUsers?: Array<{
    display_name?: string | null;
    user_email?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  }>
): boolean {
  const q = (query || "").toLowerCase().trim();
  if (!q) return true;
  const own = [shelter.shelter_name, shelter.shelter_code, shelter.state, shelter.sales_group];
  for (const v of own) {
    if ((v || "").toLowerCase().includes(q)) return true;
  }
  for (const u of attachedUsers || []) {
    const name = `${u.first_name || ""} ${u.last_name || ""} ${u.display_name || ""}`;
    if (name.toLowerCase().includes(q)) return true;
    if ((u.user_email || "").toLowerCase().includes(q)) return true;
  }
  return false;
}

// ── DFV Services list: sort + filters (admin Shelters tab) ───────────────────

interface ShelterListRow {
  shelter_code?: string | null;
  shelter_name?: string | null;
  state?: string | null;
  customer_type?: string | null;
  sales_group?: string | null;
  deactivated?: boolean | null;
}

// Value of the Sales Order Group filter that means "services with no group set".
export const NO_SALES_GROUP = "__none__";

// Sort by DFV Service Code (John, 6 Aug 2026) — the list used to come back
// name-ordered from the repo, but the code is what people scan for.
// `numeric` + `sensitivity: "base"` so a non-padded or differently-cased code
// still lands in the right place (ZephyrQLD9 before ZephyrQLD10, not after).
// Blank codes sort last rather than jumping to the top.
export function sortSheltersByCode<T extends ShelterListRow>(rows: T[]): T[] {
  return rows.slice().sort(function (a, b) {
    const ca = (a.shelter_code || "").trim();
    const cb = (b.shelter_code || "").trim();
    if (!ca && !cb) return 0;
    if (!ca) return 1;
    if (!cb) return -1;
    return ca.localeCompare(cb, undefined, { numeric: true, sensitivity: "base" });
  });
}

// Does a shelter pass the State / Customer-type-CATEGORY filters?
//
// The category filter matches the BROAD category (DFV Refuge / Intensive Support
// Org. / First Nation / Other) held on lookup_options.option_category (db-124),
// NOT the raw customer_type string — there are ~85 Unleashed types and nobody
// wants to filter by "ISO _ Micah Projects". Same approach as the CRM audience /
// Feedback / Telemarketing filters. `categoryByType` maps raw type → category.
//
// An empty filter value means "no filter". A shelter whose type has no mapped
// category can only match the empty filter — it is never silently lumped in.
export function shelterPassesListFilters(
  shelter: ShelterListRow,
  filters: { state?: string; category?: string; group?: string },
  categoryByType?: Map<string, string>
): boolean {
  const state = (filters.state || "").trim();
  if (state && (shelter.state || "").trim().toUpperCase() !== state.toUpperCase()) return false;
  const group = (filters.group || "").trim();
  if (group) {
    const own = (shelter.sales_group || "").trim();
    if (group === NO_SALES_GROUP ? own !== "" : own !== group) return false;
  }
  const category = (filters.category || "").trim();
  if (!category) return true;
  const mapped = categoryByType?.get((shelter.customer_type || "").trim());
  return (mapped || "") === category;
}

// The distinct states present in the loaded services, sorted — so the dropdown
// only ever offers states that would actually return rows.
export function statesFromShelters(rows: ShelterListRow[]): string[] {
  const set = new Set<string>();
  for (const r of rows) {
    const s = (r.state || "").trim().toUpperCase();
    if (s) set.add(s);
  }
  return [...set].sort();
}

// Test/dummy services (code starts "TEST") are left out of the DFV Services list,
// its counts and its CSV. Same rule as the CRM audience (isTestCode).
export function isTestShelter(shelter: { shelter_code?: string | null }): boolean {
  return isTestCode(shelter.shelter_code);
}

// Deactivated services are hidden unless "Show deactivated" is ticked — OR the admin
// is searching, so a deactivated service can still be found by name (otherwise it
// would look as if it had vanished). Active services always pass.
export function shelterPassesDeactivatedFilter(
  shelter: { deactivated?: boolean | null },
  showDeactivated: boolean,
  query: string
): boolean {
  if (!shelter.deactivated) return true;
  return showDeactivated || (query || "").trim() !== "";
}

// The distinct Sales Order Group values present in the loaded services, sorted, so the
// dropdown only offers groups that would return rows. `hasNone` = at least one service
// has no group (the dropdown then also offers "(none set)").
export function salesGroupsFromShelters(rows: ShelterListRow[]): {
  groups: string[];
  hasNone: boolean;
} {
  const set = new Set<string>();
  let hasNone = false;
  for (const r of rows) {
    const g = (r.sales_group || "").trim();
    if (g) set.add(g);
    else hasNone = true;
  }
  return {
    groups: [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
    hasNone,
  };
}

export type ShelterSortKey = "code" | "name" | "state" | "type" | "group" | "sisters";
export type ShelterSort = { key: ShelterSortKey; dir: "asc" | "desc" };

// Sort the DFV Services list by any column. Blank values always sort LAST (in either
// direction) so empty rows never jump to the top. `numeric` so ZephyrQLD9 < ZephyrQLD10.
// `sistersOf` returns the text to sort the Sister services column by (e.g. the short
// codes joined); it is only called for the "sisters" key. Ties fall back to the code.
export function sortShelters<T extends ShelterListRow & { id?: string }>(
  rows: T[],
  sort: ShelterSort,
  sistersOf?: (row: T) => string
): T[] {
  const value = (r: T): string => {
    switch (sort.key) {
      case "name":
        return r.shelter_name || "";
      case "state":
        return r.state || "";
      case "type":
        return r.customer_type || "";
      case "group":
        return r.sales_group || "";
      case "sisters":
        return sistersOf ? sistersOf(r) : "";
      default:
        return r.shelter_code || "";
    }
  };
  const cmp = (a: string, b: string) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
  const sign = sort.dir === "desc" ? -1 : 1;
  return rows.slice().sort(function (a, b) {
    const va = value(a).trim();
    const vb = value(b).trim();
    if (!va && vb) return 1;
    if (va && !vb) return -1;
    const c = va && vb ? cmp(va, vb) : 0;
    if (c !== 0) return c * sign;
    return cmp((a.shelter_code || "").trim(), (b.shelter_code || "").trim());
  });
}

// The CSV table (header row + one row per service) for the DFV Services list export.
// Pass the FULL filtered list (not just the visible page). `sistersOf` as above.
export function shelterListCsvTable<T extends ShelterListRow>(
  rows: T[],
  sistersOf?: (row: T) => string
): string[][] {
  const header = [
    "DFV Service Code",
    "DFV Service Name",
    "State",
    "Customer Type",
    "Sales Order Group",
    "Sister services",
    "Deactivated",
  ];
  const body = rows.map((r) => [
    r.shelter_code || "",
    r.shelter_name || "",
    r.state || "",
    r.customer_type || "",
    r.sales_group || "",
    sistersOf ? sistersOf(r) : "",
    r.deactivated ? "Yes" : "No",
  ]);
  return [header, ...body];
}

// Map of shelter_code → its active workers, derived from memberships + profiles.
// A user appears at most once per shelter even if duplicate membership rows exist.
export function activeWorkersByShelter(
  memberships: LifecycleMembership[],
  profiles: LifecycleProfile[]
): Map<string, WorkerRef[]> {
  const byId = new Map<string, LifecycleProfile>(profiles.map((p) => [p.id, p]));
  const out = new Map<string, WorkerRef[]>();
  for (const m of memberships) {
    const p = byId.get(m.user_id);
    if (!isActiveWorkerProfile(p)) continue;
    const list = out.get(m.shelter_code) ?? [];
    if (!list.some((w) => w.id === p.id)) list.push(toWorkerRef(p));
    out.set(m.shelter_code, list);
  }
  return out;
}

// Convenience: active worker count for a single shelter.
export function activeWorkerCount(
  shelterCode: string,
  memberships: LifecycleMembership[],
  profiles: LifecycleProfile[]
): number {
  return (activeWorkersByShelter(memberships, profiles).get(shelterCode) ?? []).length;
}

// Decision 1: deactivating a worker should warn (and offer to deactivate the
// shelter) when it would leave any of that worker's shelters with no active
// workers. Returns the list of shelter codes that would be orphaned. A user who
// is already deactivated, or is not a shelter worker, orphans nothing.
export function assessUserDeactivation(
  targetUserId: string,
  memberships: LifecycleMembership[],
  profiles: LifecycleProfile[]
): UserDeactivationAssessment {
  const target = profiles.find((p) => p.id === targetUserId);
  if (!isActiveWorkerProfile(target)) return { orphanedShelters: [] };

  const targetShelters = new Set(
    memberships.filter((m) => m.user_id === targetUserId).map((m) => m.shelter_code)
  );
  const workers = activeWorkersByShelter(memberships, profiles);
  const orphanedShelters: string[] = [];
  for (const code of targetShelters) {
    const remaining = (workers.get(code) ?? []).filter((w) => w.id !== targetUserId);
    if (remaining.length === 0) orphanedShelters.push(code);
  }
  return { orphanedShelters };
}

// Workers eligible for the deactivation CASCADE (decision 2): the shelter's active
// workers whose ONLY shelter is this one. A worker who also belongs to another
// shelter is EXCLUDED — retiring one shelter must not revoke their access to the
// others; they simply lose this (now-inactive) shelter. These are the workers the
// confirm modal lists and the cascade deactivates.
export function cascadeWorkersForShelter(
  shelterCode: string,
  memberships: LifecycleMembership[],
  profiles: LifecycleProfile[]
): WorkerRef[] {
  const shelterWorkers = activeWorkersByShelter(memberships, profiles).get(shelterCode) ?? [];
  return shelterWorkers.filter((w) => membershipCodeCount(w.id, memberships) <= 1);
}

// The complement of cascadeWorkersForShelter: active workers of the shelter who
// ALSO belong to another shelter. On a shelter deactivation these are DETACHED
// from this shelter only (their membership row for this shelter is removed) — the
// account and other-shelter access are preserved.
export function sharedWorkersForShelter(
  shelterCode: string,
  memberships: LifecycleMembership[],
  profiles: LifecycleProfile[]
): WorkerRef[] {
  const shelterWorkers = activeWorkersByShelter(memberships, profiles).get(shelterCode) ?? [];
  return shelterWorkers.filter((w) => membershipCodeCount(w.id, memberships) > 1);
}

function membershipCodeCount(userId: string, memberships: LifecycleMembership[]): number {
  return new Set(memberships.filter((m) => m.user_id === userId).map((m) => m.shelter_code)).size;
}

// Decision 2 + 4: assess deactivating a shelter — list its active workers (to be
// cascade-deactivated and shown to the admin) and count its in-flight requests
// (informational; not cancelled).
export function assessShelterDeactivation(
  shelterCode: string,
  memberships: LifecycleMembership[],
  profiles: LifecycleProfile[],
  requests: LifecycleRequest[]
): ShelterDeactivationAssessment {
  const activeWorkers = activeWorkersByShelter(memberships, profiles).get(shelterCode) ?? [];
  const inflightRequestCount = requests.filter(
    (r) => r.shelter_code === shelterCode && ACTIVE_STATUS_VALUES.includes(r.status ?? "")
  ).length;
  return { activeWorkers, inflightRequestCount };
}
